import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { UploadFortschrittProps } from "../Upload/UploadFortschritt.tsx";

/**
 * Der Zustand der Upload-Stellen ("Upload-Sperren", Spec 2026-10-20): welche Stelle gesperrt ist,
 * weil an ihrem Objekt ein Upload laeuft, und welche Fortschrittszeile sie dazu zeigt.
 *
 * Er wohnt ueber den Komponenten, die hochladen: Ein Upload von 100 MB laeuft Minuten, der Nutzer
 * verlaesst die startende Komponente in der Zeit leicht (Reiterwechsel baut sie ab, Bestellungswechsel
 * behaelt die Instanz, ein geschlossener Drawer ist beim Oeffnen eine neue). `App.tsx` ruft
 * `useUploadStellen()` einmal auf, neben `useStehendeHinweise()`, und reicht die Instanz durch.
 *
 * Ein Store mit Abo, KEIN App-State: `onprogress` feuert ungedrosselt (`api/basis.ts`). Laege der
 * Fortschritt im State von `App.tsx`, zeichnete jeder Tick minutenlang die ganze App neu. Der Store
 * ist ein gewoehnliches Objekt, `App` selbst zeichnet bei einem Fortschritt nicht neu; die Leser
 * (`useUploadStelle`, `useUploadStellenMitPraefix`) abonnieren ihn ueber `useSyncExternalStore`. Dafuer
 * bleiben die Schnappschuesse zwischen zwei Aenderungen REFERENZGLEICH, und ein Lauf schreibt den
 * Anteil nur fort, wenn sich der ganze Prozentwert aendert (`UploadFortschritt` rundet ohnehin ab).
 *
 * Ein Lauf (`UploadLauf`) beginnt an einer Stelle und ist der Griff fuer "Anzeige setzen" und
 * "beenden". Zwei Arten zu beginnen: `beginne` zaehlt (zwei Laeufe duerfen sich ueberlappen, gesperrt
 * bleibt bis zum Ende des letzten), `versucheBeginn` ist exklusiv und prueft SYNCHRON - ein
 * Doppelklick vor dem Neuzeichnen darf keinen zweiten Upload starten. `beende` ist idempotent und
 * loescht nur die Anzeige, die der Lauf selbst gesetzt hat; ein `zeige` nach dem Ende wirkt nicht mehr
 * (ein verspaeteter Fortschritts-Rueckruf der Upload-Funktion bleibt so harmlos).
 */
export interface UploadStellenAnzeige extends UploadFortschrittProps {
  /** Frei waehlbare Marke, etwa der Dokumenttyp: das Grid zeigt die Zeile nur im Menue dieses Typs. */
  marke?: string;
}

export interface UploadStellenStand {
  readonly gesperrt: boolean;
  readonly anzeige: UploadStellenAnzeige | null;
}

export interface UploadStellenEintrag {
  readonly stelle: string;
  readonly stand: UploadStellenStand;
}

/** Der Stand jeder Stelle, an der nichts laeuft. Eingefroren und EINE Instanz: dient als Vergleichswert. */
export const UPLOAD_STELLE_FREI: UploadStellenStand = Object.freeze({ gesperrt: false, anzeige: null });

export interface UploadLauf {
  /** Fortlaufend je Store, ab 1. */
  readonly kennung: number;
  readonly stelle: string;
  /** Setzt die Anzeige des Laufs; `null` nimmt sie weg, aber nur, wenn sie von diesem Lauf stammt. Nach `beende()` wirkungslos. */
  zeige(anzeige: UploadStellenAnzeige | null): void;
  /** Idempotent: nur der erste Aufruf zaehlt die Stelle herunter. */
  beende(): void;
}

export interface UploadStellen {
  /** Zaehlend: mehrere Laeufe an einer Stelle, gesperrt bis zum Ende des letzten. */
  beginne(stelle: string): UploadLauf;
  /** Exklusiv und synchron: `null`, wenn an der Stelle schon ein Lauf besteht. */
  versucheBeginn(stelle: string): UploadLauf | null;
  /** Synchron, fuer Handler (ein Render-Wert waere erst nach dem naechsten Zeichnen gueltig). */
  istGesperrt(stelle: string): boolean;
  /** Referenzgleich bis zur naechsten Aenderung DIESER Stelle; unbekannte Stellen: `UPLOAD_STELLE_FREI`. */
  stand(stelle: string): UploadStellenStand;
  /** Nur gesperrte Stellen; referenzgleich bis zur naechsten Aenderung einer passenden Stelle. */
  staendeMitPraefix(praefix: string): readonly UploadStellenEintrag[];
  /** Meldet jede Aenderung; die Abmeldung ist der Rueckgabewert. */
  abonniere(zuhoerer: () => void): () => void;
  /** Meldet das Ende jedes Laufs (genau einmal je Lauf), nachdem die Stelle heruntergezaehlt ist. */
  beiEnde(zuhoerer: (stelle: string, kennung: number) => void): () => void;
}

/** Die Schluessel der Stellen: ein Bauhelfer je Art, damit jede Oberflaeche denselben Schluessel bildet. */
export const uploadStelle = {
  bestellung: (bestellungId: string): string => `bestellung:${bestellungId}`,
  position: (bestellungId: string, jtlPosId: number): string => `position:${bestellungId}:${jtlPosId}`,
  /** Praefix aller Positionsstellen einer Bestellung; der Doppelpunkt am Ende trennt `o1` von `o10`. */
  positionenVon: (bestellungId: string): string => `position:${bestellungId}:`,
  sammelsendung: (shipmentId: string): string => `sammelsendung:${shipmentId}`,
  kurssicherung: (dealId: string): string => `kurssicherung:${dealId}`,
};

/** Was der Store je Stelle fuehrt, solange mindestens ein Lauf besteht. */
interface Zelle {
  laeufe: number;
  /** Die gezeigte Anzeige (bei gleichem ganzen Prozentwert die aeltere: nur Aenderungen ersetzen sie). */
  anzeige: UploadStellenAnzeige | null;
  /** Kennung des Laufs, der die Anzeige zuletzt gesetzt hat; nur er loescht sie wieder. */
  besitzer: number | null;
  stand: UploadStellenStand;
  eintrag: UploadStellenEintrag;
}

const KEINE_EINTRAEGE: readonly UploadStellenEintrag[] = Object.freeze([]);

/** Gleich im Sinne der Meldung: der GANZE Prozentwert, nicht der Anteil, und alle uebrigen Felder. */
function gleichAngezeigt(a: UploadStellenAnzeige | null, b: UploadStellenAnzeige | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    Math.floor(a.anteil * 100) === Math.floor(b.anteil * 100) &&
    a.dateiname === b.dateiname &&
    a.phase === b.phase &&
    a.nummer === b.nummer &&
    a.gesamt === b.gesamt &&
    a.marke === b.marke
  );
}

export function erzeugeUploadStellen(): UploadStellen {
  const zellen = new Map<string, Zelle>();
  // Praefix -> letzte Trefferliste. Nur nicht-leere Listen werden gemerkt; jede Aenderung einer
  // passenden Stelle verwirft den Eintrag. So waechst der Speicher nicht mit den je gefragten Praefixen.
  const praefixTreffer = new Map<string, readonly UploadStellenEintrag[]>();
  // Eigene Huelle je Abo, damit dieselbe Funktion zweimal angemeldet werden kann und jede Abmeldung nur ihr Abo trifft.
  const abonnenten = new Set<{ lies: () => void }>();
  const enden = new Set<{ lies: (stelle: string, kennung: number) => void }>();
  let naechsteKennung = 1;

  function verwerfePraefixe(stelle: string): void {
    for (const praefix of [...praefixTreffer.keys()]) {
      if (stelle.startsWith(praefix)) praefixTreffer.delete(praefix);
    }
  }

  /** Baut den Schnappschuss der Zelle nur dann neu, wenn sich Sperre oder Anzeige geaendert haben. */
  function uebernehmeStand(stelle: string, zelle: Zelle): void {
    const gesperrt = zelle.laeufe > 0;
    if (zelle.stand.gesperrt === gesperrt && zelle.stand.anzeige === zelle.anzeige) return;
    zelle.stand = { gesperrt, anzeige: zelle.anzeige };
    zelle.eintrag = { stelle, stand: zelle.stand };
    verwerfePraefixe(stelle);
  }

  function meldeAbonnenten(): void {
    for (const abo of [...abonnenten]) abo.lies();
  }

  function lauf(stelle: string): UploadLauf {
    const kennung = naechsteKennung++;
    let zelle = zellen.get(stelle);
    if (!zelle) {
      zelle = {
        laeufe: 0,
        anzeige: null,
        besitzer: null,
        stand: UPLOAD_STELLE_FREI,
        eintrag: { stelle, stand: UPLOAD_STELLE_FREI },
      };
      zellen.set(stelle, zelle);
    }
    zelle.laeufe += 1;
    uebernehmeStand(stelle, zelle);
    meldeAbonnenten();

    let beendet = false;
    return {
      kennung,
      stelle,
      zeige(anzeige) {
        if (beendet) return;
        const aktuell = zellen.get(stelle);
        if (!aktuell) return;
        if (anzeige === null) {
          // Nur die eigene Anzeige: ein anderer Lauf an der Stelle behaelt seine Zeile.
          if (aktuell.besitzer !== kennung) return;
          aktuell.besitzer = null;
          aktuell.anzeige = null;
        } else {
          // Der Besitz wandert auch bei einer unveraenderten Anzeige: wer zuletzt zeigt, loescht sie beim Ende.
          aktuell.besitzer = kennung;
          if (gleichAngezeigt(aktuell.anzeige, anzeige)) return;
          // Kopie: ein Aufrufer, der sein Objekt weiter veraendert, darf den Schnappschuss nicht mitaendern.
          aktuell.anzeige = { ...anzeige };
        }
        uebernehmeStand(stelle, aktuell);
        meldeAbonnenten();
      },
      beende() {
        if (beendet) return;
        beendet = true;
        const aktuell = zellen.get(stelle);
        if (aktuell) {
          aktuell.laeufe -= 1;
          if (aktuell.besitzer === kennung) {
            aktuell.besitzer = null;
            aktuell.anzeige = null;
          }
          if (aktuell.laeufe > 0) {
            uebernehmeStand(stelle, aktuell);
          } else {
            zellen.delete(stelle);
            verwerfePraefixe(stelle);
          }
        }
        for (const ende of [...enden]) ende.lies(stelle, kennung);
        meldeAbonnenten();
      },
    };
  }

  return {
    beginne: lauf,
    versucheBeginn(stelle) {
      return zellen.has(stelle) ? null : lauf(stelle);
    },
    istGesperrt(stelle) {
      return zellen.has(stelle);
    },
    stand(stelle) {
      return zellen.get(stelle)?.stand ?? UPLOAD_STELLE_FREI;
    },
    staendeMitPraefix(praefix) {
      const gemerkt = praefixTreffer.get(praefix);
      if (gemerkt) return gemerkt;
      const treffer: UploadStellenEintrag[] = [];
      for (const [stelle, zelle] of zellen) {
        if (stelle.startsWith(praefix)) treffer.push(zelle.eintrag);
      }
      if (treffer.length === 0) return KEINE_EINTRAEGE;
      praefixTreffer.set(praefix, treffer);
      return treffer;
    },
    abonniere(zuhoerer) {
      const abo = { lies: zuhoerer };
      abonnenten.add(abo);
      return () => { abonnenten.delete(abo); };
    },
    beiEnde(zuhoerer) {
      const ende = { lies: zuhoerer };
      enden.add(ende);
      return () => { enden.delete(ende); };
    },
  };
}

/**
 * Der Store von `App.tsx`: einmal je Komponente erzeugt (`useState`-Initialisierer) und
 * referenzstabil. Eine Komponente ohne durchgereichten Store legt sich damit einen eigenen an:
 * Sperre und Anzeige leben dann nur so lange wie diese Komponente, so wie vor den Upload-Sperren.
 */
export function useUploadStellen(): UploadStellen {
  const [stellen] = useState(erzeugeUploadStellen);
  return stellen;
}

/** Sperre und Anzeige EINER Stelle; `null` (noch kein Objekt gezeigt) ist `UPLOAD_STELLE_FREI`. */
export function useUploadStelle(stellen: UploadStellen, stelle: string | null): UploadStellenStand {
  const abonnieren = useCallback((zuhoerer: () => void) => stellen.abonniere(zuhoerer), [stellen]);
  const lesen = useCallback(
    (): UploadStellenStand => (stelle === null ? UPLOAD_STELLE_FREI : stellen.stand(stelle)),
    [stellen, stelle],
  );
  return useSyncExternalStore(abonnieren, lesen);
}

/** Alle gesperrten Stellen unter einem Praefix, etwa `uploadStelle.positionenVon(bestellungId)`. */
export function useUploadStellenMitPraefix(stellen: UploadStellen, praefix: string): readonly UploadStellenEintrag[] {
  const abonnieren = useCallback((zuhoerer: () => void) => stellen.abonniere(zuhoerer), [stellen]);
  const lesen = useCallback(() => stellen.staendeMitPraefix(praefix), [stellen, praefix]);
  return useSyncExternalStore(abonnieren, lesen);
}

/**
 * Ruft `beiFremdemEnde`, wenn an `stelle` ein Lauf endet, den diese Komponente NICHT mit `alsEigen`
 * angemeldet hat: eine abgebaute Instanz hat ihn gestartet, und das Ergebnis (neue Checkliste,
 * neue Dokumente) kennt diese Instanz noch nicht. Eigene Laeufe laden selbst nach, sie werden nicht
 * doppelt gemeldet.
 *
 * `beiFremdemEnde` steht in einer Ref: ein neuer Rueckruf je Render loest kein neues Abo aus. `alsEigen`
 * ist referenzstabil und gibt denselben Lauf zurueck, damit es um `beginne(...)` herum passt:
 * `alsEigen(stellen.beginne(stelle))`.
 */
export function useFremdesUploadEnde(
  stellen: UploadStellen,
  stelle: string | null,
  beiFremdemEnde: () => void,
): (lauf: UploadLauf) => UploadLauf {
  const rueckruf = useRef(beiFremdemEnde);
  useEffect(() => {
    rueckruf.current = beiFremdemEnde;
  });
  const [eigene] = useState(() => new Set<number>());

  useEffect(
    // Auch bei `stelle === null` angemeldet: das Ende eines eigenen Laufs raeumt seine Kennung ab.
    () =>
      stellen.beiEnde((endeStelle, kennung) => {
        if (eigene.delete(kennung)) return;
        if (stelle !== null && endeStelle === stelle) rueckruf.current();
      }),
    [stellen, stelle, eigene],
  );

  return useCallback((lauf: UploadLauf): UploadLauf => {
    eigene.add(lauf.kennung);
    return lauf;
  }, [eigene]);
}
